import React, { useState } from 'react'

import { Plus, Music } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import addIcon from '@/assets/images/icons/add.png'
import groupsModalImg from '@/assets/images/modals/modal-groups.png'
import { PaginatedResponse } from '@/core/api/dtos/common/types'
import { Group } from '@/core/api/entities/group'
import { Zone } from '@/core/api/entities/zone/zone'
// import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import { PERMISSIONS } from '@/core/api/services/permissionService'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import {
	// useGroups,
	useCreateGroup,
	useUpdateGroupZones,
	useUpdateGroupSchedules,
	useUpdateGroupCampaigns,
} from '@/core/hooks/useGroup'
import { usePermission } from '@/core/hooks/usePermission'
import { useTranslation } from '@/core/i18n/useTranslation'
import { GroupCard } from '@/features/zones/components/GroupCard'
import { useGroupDeletion } from '@/features/zones/hooks/useGroupDeletion'
import { AddContentToGroupModal } from '@/features/zones/modals/AddContentToGroupModal'
import AddZoneToGroupModal from '@/features/zones/modals/AddZoneToGroupModal'
import DeleteGroupModal from '@/features/zones/modals/DeleteGroupModal'
import { Button } from '@/shared/components/atoms/Button'
import { AccountFilterDropdown } from '@/shared/components/molecules/AccountFilterDropdown/AccountFilterDropdown'
import { Card } from '@/shared/components/molecules/Card/Card'
import { PaginationControls } from '@/shared/components/molecules/PaginationControls/PaginationControls'
import { Search } from '@/shared/components/molecules/Search/Search'
import SuccessModal from '@/shared/components/molecules/SuccessModal/SuccessModal'
import { useNotificationContext } from '@/shared/context/NotificationProvider'
import { useCurrentAccount } from '@/shared/hooks/useCurrentAccount'
import { useWelcomeModal } from '@/shared/hooks/useWelcomeModal'

interface GroupTabProps {
	groups: PaginatedResponse<Group> | undefined
	isLoading: boolean
	error: Error | null
	searchTerm: string
	setSearchTerm: (value: string) => void
	pagination: {
		current: number
		pageSize: number
		onChange: (page: number) => void
		onShowSizeChange: (current: number, size: number) => void
		showQuickJumper: boolean
		showSizeChanger: boolean
	}
	refetch: () => void
	accountFilter: number | null
	setAccountFilter: (id: number | null) => void
	showAccountFilter: boolean
}

export const GroupTab: React.FC<GroupTabProps> = ({
	groups,
	isLoading,
	error,
	searchTerm,
	setSearchTerm,
	pagination,
	refetch,
	accountFilter,
	setAccountFilter,
	showAccountFilter,
}) => {
	const { t } = useTranslation('zones')
	const { t: tCommon } = useTranslation('common')
	const navigate = useNavigate()
	const { showNotification } = useNotificationContext()
	const account = useCurrentAccount()
	const { hasAnyPermission } = usePermission()
	const canCreateGroup = hasAnyPermission([
		PERMISSIONS.CREATE_GROUPS,
		PERMISSIONS.MANAGE_CHILDREN_GROUPS,
		PERMISSIONS.MANAGE_OWN_LEVEL_GROUPS,
	])

	const [createStep, setCreateStep] = useState<number>(0)
	const [tempGroup, setTempGroup] = useState<Group | null>(null)
	const [tempGroupName, setTempGroupName] = useState('')
	const [tempZones, setTempZones] = useState<Zone[]>([])

	// Welcome modal with localStorage persistence and URL parameter support
	const {
		showModal: showWelcomeModal,
		handleCloseModal: handleCloseWelcomeModal,
	} = useWelcomeModal({
		modalKey: 'groups',
	})

	// Group deletion
	const {
		selectedGroup,
		isModalVisible,
		isDeleting,
		openDeleteModal: _openDeleteModal,
		closeDeleteModal,
		handleDeleteGroup,
	} = useGroupDeletion({
		onSuccess: () => {
			refetch()
		},
	})

	// Group creation mutations
	const { mutateAsync: createGroup, isPending: isCreatingGroup } =
		useCreateGroup({
			config: useAccountConfig({}),
		})
	const { mutateAsync: updateGroupZones } = useUpdateGroupZones({
		config: useAccountConfig({}),
	})
	const { mutateAsync: updateGroupSchedules } = useUpdateGroupSchedules({
		config: useAccountConfig({}),
	})
	const { mutateAsync: updateGroupCampaigns } = useUpdateGroupCampaigns({
		config: useAccountConfig({}),
	})

	const handleAddGroup = () => {
		setCreateStep(1)
	}

	const handlePageSizeChange = (newSize: number) => {
		const currentFirstItemIndex = (pagination.current - 1) * pagination.pageSize
		const newPage = Math.floor(currentFirstItemIndex / newSize) + 1
		pagination.onShowSizeChange(newPage, newSize)
	}

	const handleZoneModalConfirm = (
		selectedZones: Zone[],
		groupName?: string
	) => {
		if (!groupName) return

		// Store temp data and move to step 2 (don't create yet)
		setTempGroupName(groupName)
		setTempZones(selectedZones)

		// Create a temporary group object for the content modal
		const tempGroupObj = Group.create({
			id: 0,
			name: groupName,
			zones: selectedZones,
			schedules: [],
			campaigns: [],
			mixes: [],
		})

		setTempGroup(tempGroupObj)
		setCreateStep(2)
	}

	const handleContentModalConfirm = async (
		scheduleIds: number[],
		campaignIds: number[]
	) => {
		try {
			// Guard against missing account ID to prevent 422 errors
			if (!account?.id) {
				console.error('Cannot create group: account ID is not available')
				showNotification({
					type: 'error',
					message: tCommon('entities.group.create.error.title'),
					description: tCommon('entities.group.create.error.description'),
				})
				setCreateStep(0)
				return
			}

			// Now create the group with all the data
			const newGroup = await createGroup({
				name: tempGroupName,
				account_id: account.id,
				music_profile_id: null,
			})

			// Assign zones, schedules, and campaigns in parallel
			await Promise.all([
				updateGroupZones({
					groupId: newGroup.id,
					data: { players: tempZones.map((z) => z.id) },
				}),
				scheduleIds.length > 0
					? updateGroupSchedules({
							groupId: newGroup.id,
							data: { schedules: scheduleIds },
						})
					: Promise.resolve(),
				campaignIds.length > 0
					? updateGroupCampaigns({
							groupId: newGroup.id,
							data: { announcements: campaignIds },
						})
					: Promise.resolve(),
			])

			showNotification({
				type: 'success',
				message: tCommon('entities.group.create.success.title'),
				description: tCommon('entities.group.create.success.description', {
					name: tempGroupName,
				}),
			})

			// Reset state
			setCreateStep(0)
			setTempGroup(null)
			setTempGroupName('')
			setTempZones([])
			refetch()
		} catch (error) {
			console.error('Error creating group:', error)
			showNotification({
				type: 'error',
				message: tCommon('entities.group.create.error.title'),
				description: tCommon('entities.group.create.error.description'),
			})
			setCreateStep(0)
		}
	}

	const handleContentModalClose = () => {
		// User cancelled on step 2, go back to zones list
		setCreateStep(0)
		setTempGroup(null)
		setTempGroupName('')
		setTempZones([])
	}

	const handleCloseCreate = () => {
		setCreateStep(0)
		setTempGroup(null)
		setTempGroupName('')
		setTempZones([])
	}

	const handleEditGroup = (group: Group) => {
		navigate(`/groups/${group.id}`)
	}

	const renderGroupCard = (group: Group) => {
		return <GroupCard key={group.id} group={group} onClick={handleEditGroup} />
	}

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between gap-4">
				<div className="flex items-center gap-3 flex-1">
					<Search
						value={searchTerm}
						onChange={setSearchTerm}
						placeholder={tCommon('entities.group.search_placeholder')}
					/>
					{showAccountFilter && (
						<div className="hidden lg:block">
							<AccountFilterDropdown
								value={accountFilter}
								onChange={setAccountFilter}
							/>
						</div>
					)}
				</div>
				<div className="hidden md:flex items-center gap-3">
					{canCreateGroup && (
						<Button variant="action" size="action" onClick={handleAddGroup}>
							{t('groups.list.createGroup')}
							<Plus className="h-4 w-4" />
						</Button>
					)}
				</div>
				<div className="md:hidden flex items-center gap-2.5 justify-end"></div>
			</div>

			{isLoading ? (
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
					{Array.from({ length: 8 }).map((_, index) => (
						<Card key={index} className="overflow-hidden border">
							<div className="p-6 space-y-4">
								<div className="h-4 bg-muted rounded animate-pulse" />
								<div className="h-6 bg-muted rounded animate-pulse w-2/3" />
								<div className="h-3 bg-muted rounded animate-pulse w-1/2" />
							</div>
						</Card>
					))}
				</div>
			) : error ? (
				<div className="p-6 border rounded-xl border-dashed bg-red-50">
					<div className="flex items-center gap-4">
						<div className="flex h-12 w-12 items-center justify-center rounded-lg bg-red-100">
							<Music className="h-6 w-6 text-red-500" />
						</div>
						<div className="flex-1">
							<h3 className="text-lg font-semibold mb-1 text-red-900">
								{t('groups.list.errorLoading')}
							</h3>
							<p className="text-sm text-red-700">
								{t('groups.list.errorDescription')}
							</p>
						</div>
					</div>
				</div>
			) : !groups?.data || groups.data.length === 0 ? (
				<div className="flex flex-col items-center justify-center px-6 bg-card-dark rounded-2xl min-h-[calc(100vh-280px)]">
					<div className="text-center space-y-4 max-w-md">
						<h3 className="text-2xl font-semibold">
							{t('groups.list.empty.title')}
						</h3>
						<p className="text-sm text-muted-foreground">
							{t('groups.list.empty.description')}
						</p>
						{canCreateGroup && (
							<Button
								variant="default"
								className="rounded-full bg-white text-black hover:bg-gray-100"
								onClick={handleAddGroup}
							>
								{t('groups.list.empty.action')}
								<Plus className="ml-2 h-4 w-4" />
							</Button>
						)}
					</div>
				</div>
			) : (
				<>
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-20 md:mb-0">
						{groups.data.map((group) => renderGroupCard(group))}
					</div>

					<PaginationControls
						currentPage={pagination.current}
						totalPages={groups.meta.pagination.total_pages}
						onPageChange={pagination.onChange}
						pageSize={pagination.pageSize}
						onPageSizeChange={handlePageSizeChange}
						className="pt-6"
					/>
				</>
			)}

			{canCreateGroup && (
				<button
					type="button"
					onClick={handleAddGroup}
					className="md:hidden fixed bottom-[110px] right-4 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-transparent shadow-lg transition hover:opacity-90"
					aria-label={t('groups.list.createGroup')}
				>
					<span
						className="h-14 w-14 bg-no-repeat"
						style={{
							backgroundImage: `url(${addIcon})`,
							backgroundSize: '157px 209px',
							backgroundPosition: '-88px -48px',
						}}
					/>
				</button>
			)}

			<DeleteGroupModal
				visible={isModalVisible}
				group={selectedGroup}
				onClose={closeDeleteModal}
				onConfirm={handleDeleteGroup}
				isDeleting={isDeleting}
			/>

			{/* Step 1: Name + Zones */}
			<AddZoneToGroupModal
				visible={createStep === 1}
				group={null}
				onClose={handleCloseCreate}
				onConfirm={handleZoneModalConfirm}
				isAdding={false}
				mode="create"
			/>

			{/* Step 2: Schedules + Campaigns */}
			{tempGroup && (
				<AddContentToGroupModal
					visible={createStep === 2}
					onClose={handleContentModalClose}
					group={tempGroup}
					initialTab="schedules"
					mode="create"
					onConfirmCreate={handleContentModalConfirm}
					isCreating={isCreatingGroup}
					showPlaylistsTab
				/>
			)}

			<SuccessModal
				visible={showWelcomeModal}
				image={groupsModalImg}
				imageAlt={tCommon('welcomeModals.groups.title')}
				title={tCommon('welcomeModals.groups.title')}
				description={tCommon('welcomeModals.groups.description')}
				primaryAction={{
					label: tCommon('welcomeModals.groups.continue'),
					onClick: handleCloseWelcomeModal,
				}}
				onClose={handleCloseWelcomeModal}
			/>
		</div>
	)
}

export default GroupTab
